import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  collection,
  deleteDoc,
  doc,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  setDoc,
  Timestamp,
  updateDoc,
  where,
} from 'firebase/firestore';
import {
  Activity,
  Check,
  Copy,
  ExternalLink,
  FileText,
  Loader2,
  Plus,
  Trash2,
  X,
} from 'lucide-react';
import { db } from '../../utils/firebase';
import { useAuth } from '../../utils/AuthContext';
import type { PortalActivity, PortalStakeholderLink } from '../../types';
import { SAMPLE_GC_PORTAL_SLUG, SAMPLE_INVITE_PURPOSE } from '../../utils/sampleGcPortal';
import {
  activityMatchesInvite,
  describeSampleActivityAction,
  formatActivityWhen,
  summarizeInviteActivity,
} from '../../utils/samplePortalInviteActivity';
import './SamplePortalInvitesModal.css';

interface SamplePortalInvitesModalProps {
  open: boolean;
  onClose: () => void;
}

type InviteRow = PortalStakeholderLink & { id: string };

function toDate(value: InviteRow['createdAt'] | InviteRow['lastUsedAt'] | undefined): Date | null {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof (value as { toDate?: () => Date }).toDate === 'function') {
    return (value as { toDate: () => Date }).toDate();
  }
  return null;
}

const SamplePortalInvitesModal: React.FC<SamplePortalInvitesModalProps> = ({ open, onClose }) => {
  const { user } = useAuth();
  const [displayName, setDisplayName] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [portalId, setPortalId] = useState<string | null>(null);
  const [portalSlug, setPortalSlug] = useState(SAMPLE_GC_PORTAL_SLUG);
  const [links, setLinks] = useState<InviteRow[]>([]);
  const [activity, setActivity] = useState<PortalActivity[]>([]);
  const [selectedLinkId, setSelectedLinkId] = useState<string | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [createdUrl, setCreatedUrl] = useState<string | null>(null);

  const loadPortal = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const portalSnap = await getDocs(
        query(
          collection(db, 'clientPortals'),
          where('slug', '==', SAMPLE_GC_PORTAL_SLUG),
          limit(1),
        ),
      );
      if (portalSnap.empty) {
        setPortalId(null);
        setLinks([]);
        setError(
          `Sample portal "${SAMPLE_GC_PORTAL_SLUG}" was not found. Run the sample GC portal clone script first.`,
        );
        return;
      }
      const portalDoc = portalSnap.docs[0];
      const pid = portalDoc.id;
      setPortalId(pid);
      setPortalSlug(portalDoc.data().slug || SAMPLE_GC_PORTAL_SLUG);

      const linksSnap = await getDocs(
        query(collection(db, 'portalStakeholderLinks'), where('portalId', '==', pid)),
      );
      const rows = linksSnap.docs
        .map(d => ({ id: d.id, ...(d.data() as PortalStakeholderLink) }))
        .filter(l => l.purpose === SAMPLE_INVITE_PURPOSE)
        .sort((a, b) => {
          const at = toDate(a.createdAt)?.getTime() || 0;
          const bt = toDate(b.createdAt)?.getTime() || 0;
          return bt - at;
        });
      setLinks(rows);
    } catch (err: any) {
      console.error('Load sample portal invites:', err);
      setError(err?.message || 'Could not load sample portal');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);

  useEffect(() => {
    if (!open) return;
    setDisplayName('');
    setCompanyName('');
    setError(null);
    setCreatedUrl(null);
    setCopiedId(null);
    setSelectedLinkId(null);
    void loadPortal();
  }, [open, loadPortal]);

  useEffect(() => {
    if (!open || links.length === 0) return;
    if (selectedLinkId && links.some(l => l.id === selectedLinkId)) return;
    setSelectedLinkId(links.find(l => !l.revokedAt)?.id || links[0].id);
  }, [open, links, selectedLinkId]);

  useEffect(() => {
    if (!open || !portalId) {
      setActivity([]);
      return undefined;
    }
    const q = query(
      collection(db, 'portalActivity'),
      where('portalId', '==', portalId),
      orderBy('timestamp', 'desc'),
      limit(300),
    );
    const unsub = onSnapshot(
      q,
      snap => {
        const rows: PortalActivity[] = snap.docs.map(d => {
          const data = d.data();
          return {
            id: d.id,
            portalId: data.portalId,
            projectId: data.projectId,
            sessionId: data.sessionId,
            type: data.type,
            action: data.action,
            metadata: data.metadata,
            userAgent: data.userAgent,
            source: data.source,
            timestamp: data.timestamp?.toDate?.() || new Date(),
          };
        });
        setActivity(rows.filter(a => a.source === 'stakeholder'));
      },
      err => console.warn('[SamplePortalInvites] activity subscribe:', err),
    );
    return () => unsub();
  }, [open, portalId]);

  const activityByLinkId = useMemo(() => {
    const map = new Map<string, PortalActivity[]>();
    for (const link of links) {
      map.set(link.id, activity.filter(ev => activityMatchesInvite(ev, link)));
    }
    return map;
  }, [links, activity]);

  const selectedLink = useMemo(
    () => links.find(l => l.id === selectedLinkId) || null,
    [links, selectedLinkId],
  );

  const selectedActivity = useMemo(
    () => (selectedLink ? activityByLinkId.get(selectedLink.id) || [] : []),
    [selectedLink, activityByLinkId],
  );

  const buildUrl = (token: string) =>
    `${window.location.origin}/portal/${portalSlug}?stakeholderToken=${encodeURIComponent(token)}`;

  const handleCopy = async (token: string) => {
    await navigator.clipboard.writeText(buildUrl(token));
    setCopiedId(token);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const handleCreate = async () => {
    if (!user?.id || !portalId || !displayName.trim()) return;
    setBusy(true);
    setError(null);
    setCreatedUrl(null);
    try {
      const linkRef = doc(collection(db, 'portalStakeholderLinks'));
      const company = companyName.trim();
      await setDoc(linkRef, {
        portalId,
        portalSlug,
        displayName: displayName.trim(),
        ...(company ? { companyName: company } : {}),
        purpose: SAMPLE_INVITE_PURPOSE,
        createdAt: Timestamp.now(),
        createdBy: user.id,
        revokedAt: null,
      });
      const url = buildUrl(linkRef.id);
      setCreatedUrl(url);
      setSelectedLinkId(linkRef.id);
      setDisplayName('');
      setCompanyName('');
      await navigator.clipboard.writeText(url);
      setCopiedId(linkRef.id);
      setTimeout(() => setCopiedId(null), 2500);
      await loadPortal();
    } catch (err: any) {
      console.error('Create sample invite:', err);
      setError(err?.message || 'Could not create invite link');
    } finally {
      setBusy(false);
    }
  };

  const handleRevoke = async (linkId: string) => {
    if (!window.confirm('Revoke this invite link? It will stop working immediately.')) return;
    try {
      await updateDoc(doc(db, 'portalStakeholderLinks', linkId), {
        revokedAt: Timestamp.now(),
      });
      if (selectedLinkId === linkId) {
        setSelectedLinkId(null);
      }
      await loadPortal();
    } catch (err: any) {
      console.error('Revoke sample invite:', err);
      setError(err?.message || 'Could not revoke link');
    }
  };

  const handleDelete = async (link: InviteRow) => {
    const name = link.displayName?.trim() || 'this invite';
    const msg = link.revokedAt
      ? `Delete ${name} permanently? This removes it from the list.`
      : `Delete ${name} permanently? The link will stop working and it will be removed from the list.`;
    if (!window.confirm(msg)) return;
    try {
      await deleteDoc(doc(db, 'portalStakeholderLinks', link.id));
      if (selectedLinkId === link.id) {
        setSelectedLinkId(null);
      }
      await loadPortal();
    } catch (err: any) {
      console.error('Delete sample invite:', err);
      setError(err?.message || 'Could not delete invite');
    }
  };

  if (!open) return null;

  const activeLinks = links.filter(l => !l.revokedAt);

  return (
    <div className="ops-modal-overlay spi-overlay" onClick={onClose} role="presentation">
      <div
        className="ops-modal spi-modal"
        onClick={e => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="spi-title"
      >
        <header className="spi-header">
          <div className="spi-header-main">
            <div className="spi-icon" aria-hidden>
              <FileText size={18} />
            </div>
            <div>
              <h2 id="spi-title" className="spi-title">
                Sample proposal invites
              </h2>
              <p className="spi-subtitle">
                Personalized view-only links for prospective GCs. Tracks portal activity in real time.
              </p>
            </div>
          </div>
          <button type="button" className="spi-close" onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </header>

        <div className="spi-body">
          <section className="spi-create">
            <div className="spi-create-fields">
              <div className="spi-create-field">
                <label className="ops-modal-label" htmlFor="spi-display-name">
                  Name on portal
                </label>
                <input
                  id="spi-display-name"
                  type="text"
                  className="ops-modal-input"
                  value={displayName}
                  onChange={e => setDisplayName(e.target.value)}
                  placeholder="e.g. Jordan Sample"
                  autoFocus
                />
              </div>
              <div className="spi-create-field">
                <label className="ops-modal-label" htmlFor="spi-company-name">
                  Company
                </label>
                <input
                  id="spi-company-name"
                  type="text"
                  className="ops-modal-input"
                  value={companyName}
                  onChange={e => setCompanyName(e.target.value)}
                  placeholder="e.g. Acme Construction"
                />
              </div>
            </div>
            <div className="spi-create-actions">
              <button
                type="button"
                className="ops-new-btn"
                disabled={busy || !portalId || !displayName.trim()}
                onClick={() => void handleCreate()}
              >
                {busy ? <Loader2 size={14} className="spi-spin" /> : <Plus size={14} />}
                Create &amp; copy link
              </button>
            </div>
          </section>

          {error && <div className="spi-alert error">{error}</div>}
          {createdUrl && (
            <div className="spi-alert success">
              <Check size={14} />
              Link created and copied
              <a href={createdUrl} target="_blank" rel="noreferrer">
                Open <ExternalLink size={12} />
              </a>
            </div>
          )}

          <div className="spi-split">
            <section className="spi-panel spi-invites">
              <div className="spi-panel-head">
                <h3>Invites ({activeLinks.length} active)</h3>
                {loading && <Loader2 size={14} className="spi-spin" />}
              </div>
              <div className="spi-panel-body">
                {!loading && links.length === 0 && (
                  <p className="spi-empty">No sample invites yet — create one above.</p>
                )}
                <ul>
                  {links.map(link => {
                    const revoked = !!link.revokedAt;
                    const stats = summarizeInviteActivity(activityByLinkId.get(link.id) || []);
                    const selected = selectedLinkId === link.id;
                    return (
                      <li key={link.id}>
                        <button
                          type="button"
                          className={`spi-invite-row${selected ? ' selected' : ''}${revoked ? ' revoked' : ''}`}
                          onClick={() => setSelectedLinkId(link.id)}
                        >
                          <div className="spi-invite-main">
                            <strong>{link.displayName}</strong>
                            {link.companyName?.trim() ? (
                              <span className="spi-invite-company">{link.companyName.trim()}</span>
                            ) : null}
                            <span>
                              Created {formatActivityWhen(toDate(link.createdAt))}
                              {link.lastUsedAt
                                ? ` · Opened ${formatActivityWhen(toDate(link.lastUsedAt))}`
                                : ' · Not opened yet'}
                              {revoked ? ' · Revoked' : ''}
                            </span>
                            {stats.eventCount > 0 && (
                              <span className="spi-invite-stats">
                                <Activity size={11} />
                                {stats.eventCount} events
                                {stats.lastAt ? ` · last ${formatActivityWhen(stats.lastAt)}` : ''}
                              </span>
                            )}
                          </div>
                        </button>
                        <div className="spi-invite-actions">
                          {!revoked && (
                            <>
                              <button type="button" onClick={() => void handleCopy(link.id)} title="Copy URL">
                                {copiedId === link.id ? <Check size={14} /> : <Copy size={14} />}
                                Copy
                              </button>
                              <a href={buildUrl(link.id)} target="_blank" rel="noreferrer" title="Open link">
                                <ExternalLink size={14} />
                                Open
                              </a>
                              <button
                                type="button"
                                className="danger"
                                onClick={() => void handleRevoke(link.id)}
                              >
                                Revoke
                              </button>
                            </>
                          )}
                          <button
                            type="button"
                            className={revoked ? 'danger-solid' : 'danger'}
                            onClick={() => void handleDelete(link)}
                            title="Delete invite"
                          >
                            <Trash2 size={14} />
                            Delete
                          </button>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </section>

            <section className="spi-panel spi-activity">
              <div className="spi-panel-head">
                <h3>Activity</h3>
              </div>
              <div className="spi-panel-body">
                {!selectedLink && (
                  <p className="spi-empty">
                    Select an invite to see how they&apos;re using the sample portal.
                  </p>
                )}
                {selectedLink && selectedActivity.length === 0 && (
                  <p className="spi-empty">
                    No activity yet for <strong>{selectedLink.displayName}</strong>.
                  </p>
                )}
                {selectedLink && selectedActivity.length > 0 && (
                  <ul className="spi-activity-list">
                    {selectedActivity.slice(0, 40).map(ev => (
                      <li key={ev.id}>
                        <strong>{describeSampleActivityAction(ev.action)}</strong>
                        <span>{formatActivityWhen(ev.timestamp)}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SamplePortalInvitesModal;
